'use client';

import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { MultiAccountTicket } from '@/components/trade/multi-account-ticket';
import type { Side, TicketInstrument } from '@/components/trade/order-ticket';
import { listInstruments } from '@/lib/api/admin-instruments';
import { addWatchlistItem, getWatchlist, listWatchlists, removeWatchlistItem } from '@/lib/api/admin-watchlists';
import { formatPrice } from '@/lib/math/price';
import { useLiveQuotes } from '@/lib/streaming/use-live-quotes';
import type { LiveQuote } from '@/lib/streaming/use-live-quote';
import { tickFlashClass, useTickFlash } from '@/lib/streaming/use-tick-flash';
import { cn } from '@/lib/util/cn';

/** Same key as the Terminal, so both show the same selected list. */
const LS_ACTIVE = 'te:terminal:watchlist';

export function WatchlistDock({ className }: { className?: string }): React.ReactElement {
  const qc = useQueryClient();
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const [ticket, setTicket] = React.useState<{ inst: TicketInstrument; side: Side } | null>(null);

  const listQ = useQuery({ queryKey: ['watchlists'], queryFn: ({ signal }) => listWatchlists(signal) });
  const lists = React.useMemo(() => listQ.data?.data ?? [], [listQ.data]);

  React.useEffect(() => {
    if (activeId && lists.some((w) => w.id === activeId)) {return;}
    let saved: string | null = null;
    try {
      saved = localStorage.getItem(LS_ACTIVE);
    } catch {
      /* ignore */
    }
    const next = lists.find((w) => w.id === saved)?.id ?? lists[0]?.id ?? null;
    if (next) {setActiveId(next);}
  }, [lists, activeId]);
  const choose = (id: string) => {
    setActiveId(id);
    try {
      localStorage.setItem(LS_ACTIVE, id);
    } catch {
      /* ignore */
    }
  };

  const detailQ = useQuery({
    queryKey: ['watchlist', activeId],
    queryFn: ({ signal }) => getWatchlist(activeId as string, signal),
    enabled: !!activeId,
  });
  const items = React.useMemo(() => detailQ.data?.data.items ?? [], [detailQ.data]);
  const ids = React.useMemo(() => items.map((i) => i.instrument_id), [items]);
  const { quotes } = useLiveQuotes(ids, 'touchline');

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['watchlist', activeId] });
    qc.invalidateQueries({ queryKey: ['watchlists'] });
  };
  const addM = useMutation({
    mutationFn: (instrumentId: string) => addWatchlistItem(activeId as string, instrumentId),
    onSuccess: invalidate,
  });
  const removeM = useMutation({
    mutationFn: (instrumentId: string) => removeWatchlistItem(activeId as string, instrumentId),
    onSuccess: invalidate,
  });

  return (
    <aside className={cn('flex h-full w-[22rem] shrink-0 flex-col border-r border-border bg-bg-elev', className)}>
      <AddSearch existingIds={ids} onAdd={(id) => addM.mutate(id)} disabled={!activeId || addM.isPending} />

      <div className="min-h-0 flex-1 overflow-y-auto">
        {detailQ.isLoading || listQ.isLoading ? (
          <div className="space-y-2 p-3">
            {Array.from({ length: 6 }, (_, i) => <Skeleton key={i} className="h-7 w-full" />)}
          </div>
        ) : items.length === 0 ? (
          <p className="px-4 py-8 text-center text-[13px] text-fg-subtle">
            Search above to add instruments.
          </p>
        ) : (
          items.map((it) => {
            const inst: TicketInstrument = {
              id: it.instrument_id,
              symbol: it.instrument_symbol ?? it.instrument_id,
              segment_id: it.instrument_segment_id ?? '',
              lot_size: it.instrument_lot_size ?? 1,
              tick_size: it.instrument_tick_size ?? 0.05,
            };
            return (
              <DockRow
                key={it.instrument_id}
                inst={inst}
                quote={quotes[it.instrument_id] ?? null}
                onTrade={(side) => setTicket({ inst, side })}
                onRemove={() => removeM.mutate(it.instrument_id)}
              />
            );
          })
        )}
      </div>

      <nav className="flex shrink-0 items-center gap-1 overflow-x-auto border-t border-border px-2 py-1.5" aria-label="Watchlists">
        {lists.map((w) => (
          <button
            key={w.id}
            type="button"
            onClick={() => choose(w.id)}
            className={cn(
              'shrink-0 rounded px-2.5 py-1 text-[13px] transition-colors',
              w.id === activeId ? 'bg-surface-inset font-medium text-fg' : 'text-fg-subtle hover:text-fg',
            )}
          >
            {w.name}
          </button>
        ))}
      </nav>

      <MultiAccountTicket
        open={ticket !== null}
        onOpenChange={(o) => {
          if (!o) {setTicket(null);}
        }}
        instrument={ticket?.inst ?? null}
        side={ticket?.side ?? 'BUY'}
        quote={ticket ? quotes[ticket.inst.id] ?? null : null}
      />
    </aside>
  );
}

function DockRow({
  inst,
  quote,
  onTrade,
  onRemove,
}: {
  inst: TicketInstrument;
  quote: LiveQuote | null;
  onTrade: (s: Side) => void;
  onRemove: () => void;
}) {
  const ltp = quote?.ltp ?? quote?.close ?? null;
  const flash = useTickFlash(quote?.ltp ?? null);
  const close = quote?.close ?? null;
  const pct =
    quote?.ltp != null && close ? ((quote.ltp - close) / close) * 100 : quote?.pct_change ?? null;
  const dir = pct == null ? 'flat' : pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat';
  return (
    <div className="group relative flex h-9 items-center justify-between gap-3 border-b border-divider px-4 text-[13px] hover:bg-surface-inset">
      <span className={cn('min-w-0 truncate', dir === 'up' ? 'text-long' : dir === 'down' ? 'text-short' : 'text-fg')}>
        {inst.symbol}
      </span>
      <span className="flex shrink-0 items-baseline gap-3 tabular-nums group-hover:invisible">
        <span className="text-fg-subtle">
          {pct != null ? `${pct > 0 ? '+' : ''}${pct.toFixed(2)}%` : ''}
        </span>
        <span key={flash.seq} className={cn('w-20 text-right font-medium text-fg', tickFlashClass(flash))}>
          {ltp != null ? formatPrice(ltp, inst.tick_size) : '—'}
        </span>
      </span>
      <span className="invisible absolute right-3 flex items-center gap-1 group-hover:visible group-focus-within:visible">
        <Button variant="buy" size="xs" className="h-6 px-2" onClick={() => onTrade('BUY')}>B</Button>
        <Button variant="sell" size="xs" className="h-6 px-2" onClick={() => onTrade('SELL')}>S</Button>
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${inst.symbol}`}
          className="grid h-6 w-6 place-items-center rounded text-fg-subtle hover:bg-surface hover:text-fg"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </span>
    </div>
  );
}

function AddSearch({
  existingIds,
  onAdd,
  disabled,
}: {
  existingIds: string[];
  onAdd: (id: string) => void;
  disabled: boolean;
}) {
  const [q, setQ] = React.useState('');
  const [debounced, setDebounced] = React.useState('');
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 250);
    return () => clearTimeout(t);
  }, [q]);
  const res = useQuery({
    queryKey: ['instr-search', debounced],
    queryFn: ({ signal }) => listInstruments({ q: debounced, limit: 100 }, signal),
    enabled: debounced.trim().length >= 1,
  });
  const results = React.useMemo(() => {
    const raw = res.data?.data ?? [];
    const cash = raw.filter((i) => i.expiry === null);
    const derivs = raw
      .filter((i) => i.expiry !== null)
      .sort(
        (a, b) =>
          Date.parse(a.expiry as string) - Date.parse(b.expiry as string) ||
          (a.strike ?? 0) - (b.strike ?? 0) ||
          a.symbol.localeCompare(b.symbol),
      );
    return [...cash, ...derivs].slice(0, 40);
  }, [res.data]);

  return (
    <div className="relative shrink-0 border-b border-border">
      <div className="flex h-11 items-center gap-2 px-4">
        <Search className="h-4 w-4 shrink-0 text-fg-subtle" aria-hidden />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search eg: nifty fut, gold mcx, infy"
          className="h-full min-w-0 flex-1 bg-transparent text-[13px] outline-none placeholder:text-fg-subtle"
          aria-label="Search instruments to add"
        />
        {q ? (
          <button type="button" onClick={() => setQ('')} className="text-fg-subtle hover:text-fg" aria-label="Clear search">
            <X className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>
      {debounced.trim().length >= 1 && q ? (
        <div className="surface-overlay absolute inset-x-2 top-full z-30 mt-1 max-h-96 overflow-y-auto p-1">
          {res.isLoading ? (
            <div className="px-3 py-2 text-[13px] text-fg-subtle">Searching…</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-2 text-[13px] text-fg-subtle">No matching instruments</div>
          ) : (
            results.map((i) => {
              const added = existingIds.includes(i.id);
              return (
                <button
                  key={i.id}
                  type="button"
                  disabled={added || disabled}
                  onClick={() => {
                    onAdd(i.id);
                    setQ('');
                  }}
                  className={cn(
                    'flex w-full items-center justify-between gap-3 rounded px-3 py-1.5 text-left text-[13px] hover:bg-surface-inset',
                    added && 'cursor-default opacity-40 hover:bg-transparent',
                  )}
                >
                  <span className="min-w-0 truncate text-fg">{i.symbol}</span>
                  <span className="flex shrink-0 items-center gap-2 text-2xs text-fg-subtle">
                    {i.expiry
                      ? new Date(i.expiry).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', timeZone: 'Asia/Kolkata' })
                      : null}
                    <span>{i.segment_id}</span>
                    {added ? 'Added' : <Plus className="h-3.5 w-3.5" aria-hidden />}
                  </span>
                </button>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
}
